import React, { useEffect, useRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useABTest } from '../../context/ABTestContext'
import abTestService from '../../services/abtest.service'
import modalIcon from '../../assets/images/icons/ab_test_modal_icon.png'
import Button from './button'
import logger from '../../utils/logger'
import { sanitizeLogMessage } from '../../utils/sanitize'

/**
 * ABTestPopup
 * Locked modal — cannot be dismissed via Escape key or overlay click.
 * User must explicitly choose a CTA.
 *
 * Props:
 *   open        {Boolean}  - Controlled open state
 *   assignment  {Object}   - Assignment object from ABTestContext (includes content)
 *   onClose     {Function} - Called after any CTA is handled, to close the popup
 *   impressionAction {String} - Optional action/source to store on popup_impression
 */
const ABTestPopup = ({ open, assignment, onClose, impressionAction }) => {
  const { markPopupDiscarded } = useABTest()
  const impressionLoggedRef = useRef(false)
  const isProcessingRef = useRef(false)

  const content = assignment?.content
  const popup = content?.popup
  const cta = content?.cta

  // Log popup_impression once when the popup opens
  useEffect(() => {
    if (open && assignment && !impressionLoggedRef.current) {
      impressionLoggedRef.current = true
      abTestService.logPopupImpression({
        test_id: String(assignment.test_id),
        popup_id: String(assignment.popup_id),
        action: impressionAction,
      }).catch(() => logger.error('ABTestPopup - impression log failed'))
    }
    if (!open) {
      impressionLoggedRef.current = false
    }
  }, [open, assignment, impressionAction])

  const handleAction = async (action) => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    try {
      await abTestService.logPopupAction({
        test_id: String(assignment.test_id),
        popup_id: String(assignment.popup_id),
        action,
      })
    } catch (err) {
      logger.error('ABTestPopup - action log error:', sanitizeLogMessage(String(err.message)))
    }

    if (action === 'Do Not Show') {
      markPopupDiscarded(assignment.test_id)
    }

    onClose(action)
  }

  const handleViewTour = () => handleAction('View Tour')
  const handleMaybeLater = () => handleAction('Maybe Later')
  const handleDoNotShow = () => handleAction('Do Not Show')

  if (!assignment || !content) return null

  return (
    <DialogPrimitive.Root open={open}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed top-1/2 left-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          aria-describedby="ab-popup-description"
        >
          {/* Icon image at top */}
          <div className="flex justify-center mb-4">
            <img src={modalIcon} alt="" className="w-20 h-20 object-contain" />
          </div>

          <DialogPrimitive.Title className="text-black font-bold text-center mb-2" style={{ fontSize: 16 }}>
            {popup?.title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description
            id="ab-popup-description"
            className="text-theme-secondary text-center mb-6"
            style={{ fontSize: 13, lineHeight: 1.6 }}
          >
            {popup?.body?.replace(/\\n/g, '\n').split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </DialogPrimitive.Description>

          <div className="flex flex-col gap-2">
            {/* Primary CTA */}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              style={{ fontSize: 14 }}
              onClick={handleViewTour}
            >
              {cta?.view_tour}
            </Button>

            {/* Secondary CTA */}
            <Button
              variant="secondary"
              size="lg"
              style={{ fontSize: 14 }}
              onClick={handleMaybeLater}
            >
              {cta?.maybe_later}
            </Button>

            {/* Tertiary CTA */}
            <button
              onClick={handleDoNotShow}
              className="w-full px-4 py-2 text-xs font-medium text-theme-primary underline-offset-2 hover:underline"
            >
              {cta?.do_not_show}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default ABTestPopup
